// Pure parser for the New-RFQ modal's "Bulk paste" affordance — extracted
// here (instead of inline in the .tsx) so the parser is:
//   1. Testable without pulling React / Vitest into the Vite build graph
//      (a .test.ts adjacent to a .tsx triggers a transitive Sentry-MCP import
//      that breaks vite build with the current @sentry/core version). Pure
//      module in shared/ sidesteps that.
//   2. Reusable by any future intake path that wants to accept a free-text
//      list of part numbers.
//
// Format accepted (all in one paste, mixable line-by-line):
//   - one PN per line:           "RS1267-5"
//   - PN + qty:                  "RS1267-5, 2"   or   "RS1267-5\t2"
//   - PN + qty + description:    "RS1267-5, 2, Hydraulic motor"  (or tabs)
// Lines starting with '#' or that are blank/whitespace-only are skipped.
// Quotes around the PN are stripped (handles CSV exports). Qty defaults to "1"
// if missing, non-numeric, zero, negative, or above MAX_QTY.
//
// Two safety nets:
//   - HEADER auto-skip: a leading row with NO digits + a column-name word is
//     dropped (handles "Part Number, Qty, Description" CSV exports cleanly).
//   - ROW cap: parser respects a `maxRows` ceiling (default 100, matches the
//     /api/rfqs server limit) and reports back whether truncation happened, so
//     the UI can surface "added 100 of 250" instead of letting the broker
//     submit an over-cap form that the server rejects.

export interface BulkPastedRow {
  partNumber: string;
  qty: string;          // kept as a string — caller hands it to a text input
  description: string;
}

export interface BulkPasteResult {
  rows: BulkPastedRow[];   // after maxRows cap
  truncated: boolean;       // totalParsed > maxRows
  totalParsed: number;      // rows the parser found BEFORE the cap
}

/** Anything larger is implausible for an aviation order and risks Number-precision
 *  loss if the qty is huge. Falls back to "1" — same as other invalid qty cases. */
const MAX_QTY = 100_000;

/** First-line "looks like a column header" check. Triggers ONLY when the line
 *  has no digits (real part numbers always include at least one digit) AND
 *  contains a recognisable header word. Both conditions together make false
 *  positives on legitimate first rows essentially impossible. */
const HEADER_HINT = /(part|number|qty|quantity|desc|description|condition|cert|p\/n|pn)/i;

export function parseBulkPaste(input: string, maxRows = 100): BulkPasteResult {
  const lines = input
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !line.startsWith('#'));

  // Strip a header row from a CSV/TSV export so the broker doesn't end up with
  // a bogus first line ("Part Number" as a PN, "Quantity" as the qty, etc.).
  if (lines.length > 0 && !/\d/.test(lines[0]!) && HEADER_HINT.test(lines[0]!)) {
    lines.shift();
  }

  const allRows: BulkPastedRow[] = lines
    .map((line) => {
      // Tab beats comma: a tab is unambiguous when pasted from a spreadsheet
      // cell range; commas can appear inside descriptions.
      const parts = line.includes('\t')
        ? line.split('\t')
        : line.includes(',')
          ? line.split(',')
          : [line];
      const partNumber = (parts[0] ?? '').trim().replace(/^["']|["']$/g, '');
      const qtyRaw = (parts[1] ?? '').trim();
      const qtyNum = Number(qtyRaw);
      const qty =
        /^\d+$/.test(qtyRaw) && qtyNum >= 1 && qtyNum <= MAX_QTY ? qtyRaw : '1';
      const description = parts.slice(2).join(', ').trim();
      return { partNumber, qty, description };
    })
    .filter((row) => row.partNumber.length > 0);

  const cap = Math.max(0, maxRows);
  return {
    rows: allRows.slice(0, cap),
    truncated: allRows.length > cap,
    totalParsed: allRows.length,
  };
}
